import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

const DomainAllowlist = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ domain: '', notes: '' });

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await fetch('/api/admin/tools/allowlist', {
        credentials: 'include'
      });
      const data = await response.json();
      setDomains(data.domains);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch allowlist');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ domain: '', notes: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/admin/tools/allowlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        resetForm();
        fetchDomains();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add domain');
      }
    } catch (err) {
      setError('Failed to add domain');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this domain from the allowlist?')) return;

    try {
      const response = await fetch(`/api/admin/tools/allowlist/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      fetchDomains();
    } catch (err) {
      alert('Failed to remove domain');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  return (
    <AdminToolsLayout>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Domain Allowlist</h5>
          <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowModal(true); }}>
            Add Domain
          </Button>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Alert variant="info" className="mb-3">
            Only domains on this list can be processed. Users attempting to process URLs from unlisted domains will see an error, and you will receive an email notification.
          </Alert>

          {domains.length === 0 ? (
            <p className="text-muted text-center py-3">No allowed domains. Add domains to enable article processing.</p>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Notes</th>
                  <th>Added By</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((domain) => (
                  <tr key={domain.id}>
                    <td><code>{domain.domain}</code></td>
                    <td>{domain.notes || '-'}</td>
                    <td>{domain.added_by || '-'}</td>
                    <td>{formatDate(domain.created_at)}</td>
                    <td>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(domain.id)}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Domain to Allowlist</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Domain</Form.Label>
              <Form.Control
                type="text"
                placeholder="example.com"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                Enter the domain without http/https (e.g., nytimes.com, washingtonpost.com)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Any notes about this domain"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="success" type="submit">
              Add Domain
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </AdminToolsLayout>
  );
};

export default DomainAllowlist;
